import * as React from 'react'
import {
  getUniqueVariantName,
  withUniqueApiNames,
  type VariantSetBlock,
} from '@basehub/data/replicache/index'
import * as PropertiesPanel from '../../properties-panel'
import { useVariantSetStore } from './store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { COLORS } from './utils'
import { Button } from '~/sotano-kit/primitives/button'
import { TrashIcon } from '@radix-ui/react-icons'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { checkIfVariantNameExists } from '@basehub/data/replicache/index'
import { getApiNameFromTitle } from '@basehub/data/api-kv/index'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { DocActionDialog } from '~/sotano-kit/primitives/confirmation-dialog'
import { useToggleState } from '~/hooks/use-toggle-state'
import { toast } from 'sonner'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'

export const VariantSetBlockPropertiesPanel = ({
  block,
  path,
}: {
  block: VariantSetBlock
  path: string
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const focusedVariantId = useVariantSetStore((s) => s.focusedVariantId)
  const setFocusedVariantId = useVariantSetStore((s) => s.setFocusedVariantId)
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const customEventsKey = React.useMemo(
    () => `${sourceBlockId}-custom-events`,
    [sourceBlockId]
  )

  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const selectedVariant = React.useMemo(() => {
    return withUniqueApiNames(block.value.variants)
      .map((v, i) => ({
        ...v,
        originalApiName: block.value.variants[i]?.apiName,
        originalLabel: block.value.variants[i]?.label,
      }))
      .find((v) => v.id === focusedVariantId)
  }, [block.value.variants, focusedVariantId])

  const handleVariantMutation = React.useCallback(
    async function <
      T extends keyof VariantSetBlock['value']['variants'][number],
    >(
      variantId: string,
      key: T,
      newValue: VariantSetBlock['value']['variants'][number][T],
      elementId: string
    ) {
      const params: UpdateBlockParams = {
        id: block.id,
        data: {
          type: block.type,
          value: {
            ...block.value,
            variants: block.value.variants.map((variant) => {
              if (variant.id === variantId) {
                return {
                  ...variant,
                  [key]: newValue,
                }
              } else {
                return variant
              }
            }),
          },
        },
        instanceChildTarget: block.instanceChildTarget,
        strategy: 'merge-deep',
        path,
        isoNow: new Date().toISOString(),
      }
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [
            {
              op: 'customWindowEvent',
              args: {
                name: customEventsKey,
                detail: {
                  type: 'focus-variant',
                  variantId,
                },
              },
            },
            getInputFocusOp(elementId),
            { op: 'updateBlock', args: params },
          ],
          undo: [
            {
              op: 'customWindowEvent',
              args: {
                name: customEventsKey,
                detail: {
                  type: 'focus-variant',
                  variantId,
                },
              },
            },
            getInputFocusOp(elementId),
            {
              op: 'blockBatchMutations',
              args: { mutations: result.undo },
            },
          ],
        })
      }
    },
    [
      block.id,
      block.instanceChildTarget,
      block.type,
      block.value,
      customEventsKey,
      getInputFocusOp,
      path,
      replicache?.mutate,
      undoManager,
    ]
  )

  React.useEffect(() => {
    const handleEvent = (e: Event) => {
      if (e instanceof CustomEvent === false) return
      if (e.detail.type === 'focus-variant') {
        setFocusedVariantId(e.detail.variantId)
      }
    }

    window.addEventListener(customEventsKey, handleEvent)
    return () => {
      window.removeEventListener(customEventsKey, handleEvent)
    }
  }, [customEventsKey, setFocusedVariantId])

  const deleteVariantToggleState = useToggleState()

  const autoGeneratedApiName = React.useMemo(
    () =>
      selectedVariant
        ? getUniqueVariantName(
            // ignore the set apiName for autogenerated
            {
              id: selectedVariant.id,
              label: selectedVariant.label,
              isDefault: selectedVariant.isDefault,
            },
            block.value.variants
          ).apiName
        : '',
    [block.value.variants, selectedVariant]
  )

  return (
    <>
      <PropertiesPanel.Group title="Selected variant">
        {selectedVariant ? (
          <>
            <PropertiesPanel.TextOption
              name="Label"
              id={`el-${sourceBlockId}-pp-label`}
              placeholder={selectedVariant.label}
              value={selectedVariant.originalLabel ?? ''}
              mutate={(newValue) =>
                handleVariantMutation(
                  selectedVariant.id,
                  'label',
                  newValue,
                  `${sourceBlockId}-pp-label`
                )
              }
            />
            <PropertiesPanel.SlugInput
              key={`slug-${selectedVariant.id}`}
              name="API Name"
              id={`el-${sourceBlockId}-pp-api-name`}
              errorMessage="API Name must be unique"
              readOnly={false}
              normalizeValue={(v) => getApiNameFromTitle(v, '')}
              entity={{
                id: selectedVariant.id,
                value: selectedVariant.originalApiName,
              }}
              checkIfAlreadyExists={(v) =>
                checkIfVariantNameExists(
                  { id: selectedVariant.id, apiName: v },
                  block.value.variants
                )
              }
              autoGenerated={
                selectedVariant.originalApiName
                  ? autoGeneratedApiName
                  : selectedVariant.apiName
              }
              mutate={(newValue) =>
                handleVariantMutation(
                  selectedVariant.id,
                  'apiName',
                  newValue ?? '',
                  `${sourceBlockId}-pp-api-name`
                )
              }
            />
            {!selectedVariant.isDefault && (
              <>
                <PropertiesPanel.BooleanOption
                  name="Hidden"
                  id={`el-${sourceBlockId}-pp-variant-hidden`}
                  value={selectedVariant.hidden}
                  mutate={(newValue) =>
                    handleVariantMutation(
                      selectedVariant.id,
                      'hidden',
                      newValue,
                      `${sourceBlockId}-pp-variant-hidden`
                    )
                  }
                />
                <PropertiesPanel.ColorPickerInput
                  id={`el-${sourceBlockId}-pp-color`}
                  options={COLORS}
                  value={selectedVariant.color}
                  name="Color"
                  mutate={(newValue) =>
                    handleVariantMutation(
                      selectedVariant.id,
                      'color',
                      newValue,
                      `${sourceBlockId}-pp-color`
                    )
                  }
                />
              </>
            )}
            <SimpleTooltip
              content="Cannot delete default variants"
              disabled={!selectedVariant.isDefault}
            >
              <Button
                intent="secondary"
                className="text-red-elevated"
                icon={<TrashIcon />}
                disabled={selectedVariant.isDefault}
                id={`el-${sourceBlockId}-pp-remove-variant`}
                onClick={deleteVariantToggleState.handleOn}
              >
                Remove variant
              </Button>
            </SimpleTooltip>
            <DocActionDialog
              title={`Are you sure you want to delete ${selectedVariant.label} from ${block.title}?`}
              description="Deleting the variant will remove all block overrides that were made for this variant in particular."
              open={deleteVariantToggleState.isOn}
              handleClose={deleteVariantToggleState.handleToggle}
              action={async () => {
                if (selectedVariant.isDefault) return
                setTimeout(() => {
                  const currIndex = block.value.variants.findIndex(
                    (v) => v.id === selectedVariant.id
                  )
                  setFocusedVariantId(
                    block.value.variants[currIndex + 1]?.id ??
                      block.value.variants[currIndex - 1]?.id ??
                      null
                  )
                }, 10)

                const params: UpdateBlockParams = {
                  id: block.id,
                  path,
                  strategy: 'merge-deep',
                  data: {
                    type: block.type,
                    value: {
                      variants: block.value.variants.filter(
                        (variant) => variant.id !== selectedVariant.id
                      ),
                    },
                  },
                  instanceChildTarget: block.instanceChildTarget,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate
                  .updateBlock(params)
                  .then((r) => {
                    toast.success(`Variant "${selectedVariant.label}" removed`)
                    return r
                  })
                  .catch((e) => {
                    if (e instanceof MutatorUserError) {
                      toast.error(e.message)
                    } else {
                      toast.error(
                        'An error occurred while deleting the variant'
                      )
                    }
                  })

                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(`el-${sourceBlockId}-pp-remove-variant`),
                      { op: 'updateBlock', args: params },
                    ],
                    undo: [
                      getInputFocusOp(undefined),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                    ],
                  })
                }
              }}
            />
          </>
        ) : (
          <span className="text-dark-faint text-xs text-pretty">
            Select a variant from the list to edit it.
          </span>
        )}
      </PropertiesPanel.Group>
    </>
  )
}
